import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from '@/utils/toast';
import i18n from '@/i18n';
import api, { isGlobalGatingHandledError } from '@/services/api';

export interface VariationSettings {
  variationsEnabled: boolean;
  variationLanguages: string[];
  autoTranslateEnabled: boolean;
}

/**
 * Fetch variation settings for a team
 */
export const useVariationSettings = (teamId: number) => {
  return useQuery<VariationSettings, AxiosError>({
    queryKey: ['variation-settings', teamId],
    queryFn: async () => {
      const response = await api.get(`/api/teams/${teamId}/variation-settings`);
      return response.data.data; // Extract from { success: true, data: {...} }
    },
    enabled: !!teamId && teamId > 0,
  });
};

/**
 * Update variation settings for a team
 */
export const useUpdateVariationSettings = () => {
  const queryClient = useQueryClient();

  return useMutation<
    VariationSettings,
    AxiosError<{ error: string }>,
    { teamId: number; data: Partial<VariationSettings> }
  >({
    mutationFn: async ({ teamId, data }) => {
      const response = await api.patch(`/api/teams/${teamId}/variation-settings`, data);
      return response.data.data; // Extract from { success: true, data: {...} }
    },
    onSuccess: (_, variables) => {
      toast.success(i18n.t('settings_variations_updated_success', { defaultValue: 'Variation settings updated successfully' }));
      queryClient.invalidateQueries({ queryKey: ['variation-settings', variables.teamId] });
    },
    onError: (error) => {
      if (isGlobalGatingHandledError(error)) return;
      toast.error(error.response?.data?.error || i18n.t('settings_variations_updated_error', { defaultValue: 'Failed to update variation settings' }));
    },
  });
};
